import {
    EDITOR_SHORTCUT,
    getXmlContentValidationMarkers,
    hasXmlParseError,
    INVALID_XML_FILE_MESSAGE,
    isLVGLPreviewableXml,
    LayoutMode,
    parseXmlWithDomParser,
    type LVGLSchemaService,
    updateEditorProp,
    SelectedFile
} from '@lved/shared-core';
import type {
    EditorStoreState,
    EditorTabContentPush,
    HoveredCodeLine,
    SelectedCodeLine,
    useEditorStore
} from '@lved/ui/store/editorStore';
import type { useFileStore } from '@lved/ui/store/fileStore';
import type { usePreviewStore } from '@lved/ui/store/previewStore';
import { useUIStore } from '@lved/ui/store/uiStore';
import { ApplicationShell, FrontendApplicationContribution, Saveable } from '@theia/core/lib/browser';
import * as lsp from '@theia/core/shared/vscode-languageserver-protocol';
import { DisposableCollection } from '@theia/core/lib/common/disposable';
import URI from '@theia/core/lib/common/uri';
import { inject, injectable } from '@theia/core/shared/inversify';
import { EditorManager, EditorWidget } from '@theia/editor/lib/browser';
import * as monaco from '@theia/monaco-editor-core';
import type { MonacoBackedEditor } from './types';
import { XmlSchemaContribution } from './xml-schema-contribution';

type StoreShape = typeof useEditorStore;
type UiModule = {
    useFileStore: typeof useFileStore;
    usePreviewStore: typeof usePreviewStore;
};
type PreviewStoreApi = ReturnType<typeof usePreviewStore.getState>;

const SOURCE_DEFAULT: SelectedCodeLine['source'] = '' as SelectedCodeLine['source'];
const SOURCE_CODE_EDITOR: SelectedCodeLine['source'] = 'CODE_EDITOR' as SelectedCodeLine['source'];
const SOURCE_PREVIEW_INSPECTOR: SelectedCodeLine['source'] = 'PREVIEW_INSPECTOR' as SelectedCodeLine['source'];

@injectable()
export class PreviewEditorBridgeContribution implements FrontendApplicationContribution {
    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(XmlSchemaContribution)
    protected readonly xmlSchemaContribution!: XmlSchemaContribution;

    protected readonly toDispose: DisposableCollection = new DisposableCollection();
    protected readonly activeEditorListener: DisposableCollection = new DisposableCollection();
    protected bridgeInstalled: boolean = false;
    protected editorStore: StoreShape | undefined;
    protected hoveredCodeLineUnsubscribe: (() => void) | undefined;
    protected selectedCodeLineUnsubscribe: (() => void) | undefined;
    protected propToUpdateUnsubscribe: (() => void) | undefined;
    protected insertCodeAsNewLineUnsubscribe: (() => void) | undefined;
    protected currentTabPathUnsubscribe: (() => void) | undefined;
    protected editorTabContentPushUnsubscribe: (() => void) | undefined;
    protected isSyncingFromEditor: boolean = false;
    protected isSyncingFromDesign: boolean = false;
    protected decorationEditor: monaco.editor.IStandaloneCodeEditor | undefined;
    protected hoverDecorations: monaco.editor.IEditorDecorationsCollection | undefined;
    protected selectedDecorations: monaco.editor.IEditorDecorationsCollection | undefined;
    protected selectedLineClearTimer: number | undefined;

    async onStart(): Promise<void> {
        await this.installPreviewEditorBridge();
    }

    protected async installPreviewEditorBridge(): Promise<void> {
        if (this.bridgeInstalled) return;
        const editorStoreModule = await import('@lved/ui/store/editorStore');
        const editorStore: StoreShape = editorStoreModule.useEditorStore;
        this.editorStore = editorStore;
        this.bridgeInstalled = true;
        this.toDispose.push({
            dispose: () => {
                if (!this.selectedLineClearTimer) return;
                window.clearTimeout(this.selectedLineClearTimer);
                this.selectedLineClearTimer = undefined;
            }
        });

        if (!this.selectedCodeLineUnsubscribe) {
            this.selectedCodeLineUnsubscribe = editorStore.subscribe(
                (state) => state.selectedCodeLine,
                (selectedCodeLine: SelectedCodeLine) => {
                    this.revealSelectedCodeLine(selectedCodeLine);
                    this.updateSelectedLineDecoration(selectedCodeLine);
                }
            );
            this.toDispose.push({
                dispose: () => {
                    this.selectedCodeLineUnsubscribe?.();
                    this.selectedCodeLineUnsubscribe = undefined;
                }
            });
        }

        if (!this.hoveredCodeLineUnsubscribe) {
            this.hoveredCodeLineUnsubscribe = editorStore.subscribe(
                (state) => state.hoveredCodeLine,
                (hoveredCodeLine: HoveredCodeLine) => {
                    this.updateHoveredLineDecoration(hoveredCodeLine);
                }
            );
            this.toDispose.push({
                dispose: () => {
                    this.hoveredCodeLineUnsubscribe?.();
                    this.hoveredCodeLineUnsubscribe = undefined;
                }
            });
        }

        if (!this.currentTabPathUnsubscribe) {
            await this.installFileTabBridge();
            this.toDispose.push({
                dispose: () => {
                    this.currentTabPathUnsubscribe?.();
                    this.currentTabPathUnsubscribe = undefined;
                }
            });
        }

        if (!this.editorTabContentPushUnsubscribe) {
            this.editorTabContentPushUnsubscribe = editorStore.subscribe(
                (state: EditorStoreState) => state.editorTabContentPush,
                (push: EditorTabContentPush) => {
                    void this.handleEditorTabContentPush(push);
                }
            );
            this.toDispose.push({
                dispose: () => {
                    this.editorTabContentPushUnsubscribe?.();
                    this.editorTabContentPushUnsubscribe = undefined;
                }
            });
        }

        if (!this.propToUpdateUnsubscribe) {
            this.propToUpdateUnsubscribe = editorStore.subscribe(
                (state) => state.propToUpdate,
                (propToUpdate: unknown) => {
                    void this.applyPropUpdate(propToUpdate);
                }
            );
            this.toDispose.push({
                dispose: () => {
                    this.propToUpdateUnsubscribe?.();
                    this.propToUpdateUnsubscribe = undefined;
                }
            });
        }

        if (!this.insertCodeAsNewLineUnsubscribe) {
            this.insertCodeAsNewLineUnsubscribe = editorStore.subscribe(
                (state: EditorStoreState) => state.insertCodeAsNewLine,
                (insertCodeAsNewLine: string) => {
                    this.insertCodeAsNewLine(insertCodeAsNewLine);
                }
            );
            this.toDispose.push({
                dispose: () => {
                    this.insertCodeAsNewLineUnsubscribe?.();
                    this.insertCodeAsNewLineUnsubscribe = undefined;
                }
            });
        }

        this.installEditorShortcutBridge();

        this.toDispose.push(this.activeEditorListener);
        this.attachActiveEditorBridge(this.editorManager.currentEditor);
        await this.syncEditorStores(this.editorManager.currentEditor);
        this.toDispose.push(
            this.editorManager.onCurrentEditorChanged((editor: EditorWidget | undefined) => {
                this.attachActiveEditorBridge(editor);
                this.syncEditorStores(editor);
            })
        );
        this.toDispose.push(
            this.editorManager.onActiveEditorChanged((editor: EditorWidget | undefined) => {
                if (!editor) return;
                this.attachActiveEditorBridge(editor);
                this.syncEditorStores(editor);
            })
        );
    }

    protected attachActiveEditorBridge(editor: EditorWidget | undefined): void {
        this.activeEditorListener.dispose();
        // Reapply preview-origin decorations against the newly active editor, if any.
        this.updateHoveredLineDecoration(
            this.editorStore?.getState().hoveredCodeLine ?? { lineNumber: 0, source: SOURCE_DEFAULT }
        );
        this.updateSelectedLineDecoration(
            this.editorStore?.getState().selectedCodeLine ?? { lineNumber: 0, source: SOURCE_DEFAULT }
        );

        if (!editor) {
            this.editorStore?.getState().setEditorInstance(null);
            return;
        }

        const resourcePath: string | undefined = this.normalizePath(editor.getResourceUri()?.path.toString());
        // The bridge only syncs XML editors into the shared preview/editor stores.
        if (!resourcePath || !resourcePath.toLowerCase().endsWith('.xml')) return;

        this.activeEditorListener.push(
            editor.editor.onDocumentContentChanged(() => {
                if (this.isSyncingFromDesign) return;
                this.syncPreviewStoreCurrentEditor(editor);
            })
        );

        // Cursor movement is treated as an explicit code-editor selection signal.
        this.activeEditorListener.push(
            editor.editor.onCursorPositionChanged((position) => {
                this.editorStore?.getState().setSelectedCodeLine({
                    lineNumber: position.line + 1,
                    source: SOURCE_CODE_EDITOR,
                    filePath: resourcePath
                });
            })
        );

        const control: monaco.editor.IStandaloneCodeEditor | undefined = (
            editor.editor as unknown as MonacoBackedEditor
        ).getControl?.();
        if (!control) return;

        this.editorStore?.getState().setEditorInstance(control);

        const model: monaco.editor.ITextModel | null = control.getModel();
        if (model) {
            this.activeEditorListener.push(
                model.onDidChangeContent((event) => {
                    if (this.isSyncingFromDesign) return;
                    if (!event.isUndoing && !event.isRedoing) return;
                    void this.syncPreviewAfterInspectorEdit(editor);
                })
            );
        }

        // Monaco mouse movement drives passive hover sync back into the preview inspector.
        this.activeEditorListener.push(
            control.onMouseMove((event) => {
                this.editorStore?.getState().setHoveredCodeLine({
                    lineNumber: event.target.position?.lineNumber || 0,
                    source: SOURCE_CODE_EDITOR,
                    filePath: resourcePath
                });
            })
        );
        this.activeEditorListener.push(
            control.onMouseLeave(() => {
                this.editorStore?.getState().setHoveredCodeLine({ lineNumber: 0, source: SOURCE_DEFAULT });
            })
        );
    }

    protected async syncEditorStores(editor: EditorWidget | undefined): Promise<void> {
        await this.syncFileStoreCurrentTab(editor);
        await this.syncPreviewStoreCurrentEditor(editor);
    }

    protected async revealSelectedCodeLine(selectedCodeLine: SelectedCodeLine): Promise<void> {
        if (selectedCodeLine.source !== SOURCE_PREVIEW_INSPECTOR) return;
        if (!selectedCodeLine.lineNumber) return;
        if (!selectedCodeLine.openRequest) {
            this.revealCurrentEditorLine(selectedCodeLine.lineNumber);
            return;
        }

        const targetPath: string | undefined = this.normalizePath(
            selectedCodeLine.filePath || this.editorManager.currentEditor?.getResourceUri()?.path.toString()
        );
        if (!targetPath) return;
        await this.editorManager.open(URI.fromFilePath(targetPath), {
            selection: {
                start: { line: selectedCodeLine.lineNumber - 1, character: 0 },
                end: { line: selectedCodeLine.lineNumber - 1, character: 0 }
            },
            revealOption: 'center'
        });
        this.editorStore?.getState().setSelectedCodeLine({
            lineNumber: 0,
            source: SOURCE_DEFAULT
        });
    }

    protected revealCurrentEditorLine(lineNumber: number): void {
        const editorContext = this.getActiveEditorControl();
        if (!editorContext) return;

        editorContext.control.revealLineInCenter(lineNumber, monaco.editor.ScrollType.Smooth);
        editorContext.control.setPosition({ lineNumber, column: 1 });
    }

    protected async applyPropUpdate(propToUpdate: unknown): Promise<void> {
        if (!propToUpdate || typeof propToUpdate !== 'object') return;
        const editorContext = this.getActiveEditorControl();
        if (!editorContext) return;

        const { control } = editorContext;
        control.pushUndoStop();
        updateEditorProp(
            propToUpdate as any,
            () => control.getModel() as any,
            (cursor, operation) => {
                control.getModel()?.pushEditOperations(cursor, [operation], () => null);
            }
        );
        control.pushUndoStop();

        this.editorStore?.getState().setPropToUpdate(null);

        await this.syncPreviewAfterInspectorEdit(this.editorManager.currentEditor);
    }

    protected installEditorShortcutBridge(): void {
        const handler = (event: Event): void => {
            this.handleEditorShortcut(event);
        };
        window.addEventListener(EDITOR_SHORTCUT, handler);
        this.toDispose.push({ dispose: () => window.removeEventListener(EDITOR_SHORTCUT, handler) });
    }

    protected handleEditorShortcut(event: Event): void {
        const cmd: string | undefined = (event as CustomEvent<{ cmd?: string }>).detail?.cmd;
        if (cmd !== 'undo' && cmd !== 'redo') return;

        const editorContext = this.getActiveEditorControl();
        if (!editorContext) return;

        editorContext.control.trigger('keyboard', cmd, null);
    }

    protected async syncPreviewAfterInspectorEdit(editor: EditorWidget | undefined): Promise<void> {
        if (!editor) return;

        const resourcePath: string | undefined = this.normalizePath(editor.getResourceUri()?.path.toString());
        if (!resourcePath?.toLowerCase().endsWith('.xml')) return;

        // Inspector edits replace editor content with isSyncingFromDesign set, so
        // onDocumentContentChanged does not push to the preview store. Always sync here;
        // auto-save remains design-mode only.
        await this.syncPreviewStoreCurrentEditor(editor);

        // Auto-save the editor content in design mode.
        if (useUIStore.getState().layoutMode !== LayoutMode.DESIGN) return;
        await Saveable.save(editor);
    }

    protected insertCodeAsNewLine(insertCodeAsNewLine: string): void {
        if (!insertCodeAsNewLine) return;

        const editorContext = this.getActiveEditorControl();
        const model: monaco.editor.ITextModel | null | undefined = editorContext?.control.getModel();
        if (editorContext && model) {
            const cursorPosition: monaco.Position | null = editorContext.control.getPosition();
            const currentLine: number =
                cursorPosition?.lineNumber || this.editorStore?.getState().selectedCodeLine.lineNumber || 1;
            const maxColumn: number = model.getLineMaxColumn(currentLine);
            const indentation: string = model.getLineContent(currentLine).match(/^\s*/)?.[0] ?? '';
            const range: monaco.Selection = new monaco.Selection(currentLine, maxColumn, currentLine, maxColumn);

            model.pushEditOperations(
                [range],
                [
                    {
                        text: `\n${indentation}${insertCodeAsNewLine}`,
                        range
                    }
                ],
                () => null
            );
        }

        this.editorStore?.getState().setInsertCodeAsNewLine('');
    }

    protected async installFileTabBridge(): Promise<void> {
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const useFileStore = module.useFileStore;
        if (!useFileStore?.subscribe) return;
        this.currentTabPathUnsubscribe = useFileStore.subscribe(
            (state) => state.currentTabPath,
            (currentTabPath: string, previousTabPath: string) => {
                void this.openCurrentTabPath(currentTabPath, previousTabPath);
            }
        );
    }

    protected async handleEditorTabContentPush(push: EditorTabContentPush): Promise<void> {
        const filePath: string | undefined = this.normalizePath(push?.path);
        const content: string | undefined = push?.content;
        const shouldSave: boolean = push?.save === true;
        if (!filePath || content === undefined || !push.seq) return;

        this.editorStore?.getState().resetEditorTabContentPush();
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const currentTabPath: string | undefined = this.normalizePath(module.useFileStore?.getState()?.currentTabPath);
        if (!currentTabPath || currentTabPath !== filePath) return;

        const editor: EditorWidget | undefined = this.editorManager.currentEditor;
        const editorPath: string | undefined = this.normalizePath(editor?.getResourceUri()?.path.toString());
        if (!editor || editorPath !== filePath) return;

        const currentContent: string | undefined = this.getEditorContent(editor);
        if (!shouldSave && currentContent === content) return;

        await this.replaceEditorContent(editor, content);

        if (shouldSave) {
            await this.syncPreviewAfterInspectorEdit(editor);
        } else {
            await this.syncPreviewStoreCurrentEditor(editor);
        }
    }

    protected replaceEditorContent(editor: EditorWidget, content: string): void {
        const document = editor.editor.document;
        const lineCount: number = document.lineCount;
        const endLine: number = Math.max(0, lineCount - 1);
        const endCharacter: number = lineCount > 0 ? document.getLineContent(lineCount).length : 0;
        const control: monaco.editor.IStandaloneCodeEditor | undefined = (
            editor.editor as unknown as MonacoBackedEditor
        ).getControl?.();
        const range: lsp.Range = {
            start: { line: 0, character: 0 },
            end: { line: endLine, character: endCharacter }
        };

        this.isSyncingFromDesign = true;
        try {
            control?.pushUndoStop();
            editor.editor.executeEdits([lsp.TextEdit.replace(range, content)]);
            control?.pushUndoStop();
        } finally {
            queueMicrotask(() => {
                this.isSyncingFromDesign = false;
            });
        }
    }

    protected async syncFileStoreCurrentTab(editor: EditorWidget | undefined): Promise<void> {
        const resourcePath: string | undefined = this.normalizePath(editor?.getResourceUri()?.path.toString());
        if (!resourcePath || !resourcePath.toLowerCase().endsWith('.xml')) return;
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        this.isSyncingFromEditor = true;
        module.useFileStore?.getState()?.openTab(resourcePath);
        queueMicrotask(() => {
            this.isSyncingFromEditor = false;
        });
    }

    protected async syncPreviewStoreCurrentEditor(editor: EditorWidget | undefined): Promise<void> {
        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const previewStore: PreviewStoreApi | undefined = module.usePreviewStore?.getState();
        const resourcePath: string | undefined = this.normalizePath(editor?.getResourceUri()?.path.toString());
        if (!resourcePath || !resourcePath.toLowerCase().endsWith('.xml')) {
            previewStore?.setRenderError('');
            previewStore?.setXmlToProcess('');
            return;
        }

        const xmlContent: string | undefined = this.getEditorContent(editor);
        if (xmlContent === undefined) return;

        if (isLVGLPreviewableXml(xmlContent)) {
            const schemaService: LVGLSchemaService | undefined = await this.xmlSchemaContribution.ensureInitialized();
            if (this.isInvalidPreviewContent(xmlContent, resourcePath, schemaService)) {
                previewStore?.setRenderError(INVALID_XML_FILE_MESSAGE);
                return;
            }
        }

        previewStore?.setXmlToProcess(xmlContent);
        previewStore?.setRenderError('');
    }

    protected async openCurrentTabPath(currentTabPath: string, previousTabPath: string | undefined): Promise<void> {
        if (this.isSyncingFromEditor) return;
        const targetPath: string | undefined = this.normalizePath(currentTabPath);
        if (targetPath?.toLowerCase().endsWith('.xml')) {
            // Always reveal/activate the editor here rather than skipping when it looks
            // already-active: editorManager.currentEditor falls back to the most-recently-visible
            // editor whenever a non-editor widget (e.g. Project Overview) is focused, so a stale
            // "already active" check would wrongly no-op the tab switch. editorManager.open() is
            // a cheap no-op on the widget itself when it's already open, so calling it unconditionally
            // is safe.
            const editor: EditorWidget | undefined = await this.editorManager.open(URI.fromFilePath(targetPath));
            // Design mode keeps the main (Monaco) panel mounted at 0 width rather than
            // detaching it, so this editor's own onDocumentContentChanged/cursor listeners
            // keep working once the panel is later restored. But activateWidget()'s focus
            // call never actually fires a DOM focus event on an element inside a zero-width
            // ancestor, so Lumino's FocusTracker never updates and onCurrentEditorChanged/
            // onActiveEditorChanged never fire in that state — the preview would otherwise
            // never sync. Sync explicitly here instead of relying on those events.
            await this.syncPreviewStoreCurrentEditor(editor);
        }

        await this.closeRemovedTabEditor(previousTabPath, targetPath);
    }

    protected async closeRemovedTabEditor(
        previousTabPath: string | undefined,
        currentTabPath: string | undefined
    ): Promise<void> {
        const previousPath: string | undefined = this.normalizePath(previousTabPath);
        if (!previousPath || previousPath === currentTabPath) return;
        if (!previousPath.toLowerCase().endsWith('.xml')) return;

        const module: UiModule = (await import('@lved/ui')) as unknown as UiModule;
        const previousTabStillOpen: boolean = module.useFileStore
            .getState()
            .openTabs.some((tab: SelectedFile): boolean => this.normalizePath(tab.filePath) === previousPath);
        if (previousTabStillOpen) return;

        const previousEditor: EditorWidget | undefined = await this.editorManager.getByUri(
            URI.fromFilePath(previousPath)
        );
        if (!previousEditor) return;

        await this.shell.closeWidget(previousEditor.id);
    }

    protected getEditorContent(editor: EditorWidget | undefined): string | undefined {
        const documentContent: string | undefined = editor?.editor?.document?.getText();
        if (documentContent !== undefined) return documentContent;

        const control: monaco.editor.IStandaloneCodeEditor | undefined = editor
            ? (editor.editor as unknown as MonacoBackedEditor).getControl?.()
            : undefined;
        return control?.getModel()?.getValue();
    }

    protected isInvalidPreviewContent(
        xmlContent: string,
        resourcePath: string,
        schemaService: LVGLSchemaService | undefined
    ): boolean {
        if (!schemaService) return hasXmlParseError(xmlContent, parseXmlWithDomParser);
        return (
            getXmlContentValidationMarkers(xmlContent, resourcePath, schemaService, parseXmlWithDomParser).length > 0
        );
    }

    protected getActiveEditorControl():
        { control: monaco.editor.IStandaloneCodeEditor; resourcePath: string } | undefined {
        const currentEditor: EditorWidget | undefined = this.editorManager.currentEditor;
        const control: monaco.editor.IStandaloneCodeEditor | undefined = currentEditor
            ? (currentEditor.editor as unknown as MonacoBackedEditor).getControl?.()
            : undefined;
        const resourcePath: string | undefined = this.normalizePath(currentEditor?.getResourceUri()?.path.toString());

        if (!control || !resourcePath) return undefined;
        return { control, resourcePath };
    }

    protected ensureDecorationCollections(control: monaco.editor.IStandaloneCodeEditor): {
        hoverDecorations: monaco.editor.IEditorDecorationsCollection;
        selectedDecorations: monaco.editor.IEditorDecorationsCollection;
    } {
        if (this.decorationEditor !== control) {
            this.hoverDecorations?.clear();
            this.selectedDecorations?.clear();
            this.decorationEditor = control;
            this.hoverDecorations = control.createDecorationsCollection();
            this.selectedDecorations = control.createDecorationsCollection();
        }

        return {
            hoverDecorations: this.hoverDecorations!,
            selectedDecorations: this.selectedDecorations!
        };
    }

    protected updateHoveredLineDecoration(hoveredCodeLine: HoveredCodeLine): void {
        const editorContext = this.getActiveEditorControl();
        if (!editorContext) {
            this.decorationEditor = undefined;
            this.hoverDecorations?.clear();
            this.selectedDecorations?.clear();
            return;
        }
        const { control, resourcePath } = editorContext;
        const { hoverDecorations } = this.ensureDecorationCollections(control);

        const shouldDecorate: boolean =
            hoveredCodeLine.lineNumber > 0 &&
            hoveredCodeLine.source === SOURCE_PREVIEW_INSPECTOR &&
            (!hoveredCodeLine.filePath || this.normalizePath(hoveredCodeLine.filePath) === resourcePath);

        hoverDecorations.set(
            shouldDecorate
                ? [
                      {
                          range: new monaco.Range(hoveredCodeLine.lineNumber, 1, hoveredCodeLine.lineNumber, 1),
                          options: {
                              isWholeLine: true,
                              className: 'lved-preview-hovered-line'
                          }
                      }
                  ]
                : []
        );
    }

    protected updateSelectedLineDecoration(selectedCodeLine: SelectedCodeLine): void {
        const editorContext = this.getActiveEditorControl();
        if (!editorContext) {
            this.decorationEditor = undefined;
            this.hoverDecorations?.clear();
            this.selectedDecorations?.clear();
            return;
        }
        const { control, resourcePath } = editorContext;
        const { selectedDecorations } = this.ensureDecorationCollections(control);

        const shouldDecorate: boolean =
            selectedCodeLine.lineNumber > 0 &&
            selectedCodeLine.source === SOURCE_PREVIEW_INSPECTOR &&
            (selectedCodeLine.openRequest === true
                ? !selectedCodeLine.filePath || this.normalizePath(selectedCodeLine.filePath) === resourcePath
                : true);

        selectedDecorations.set(
            shouldDecorate
                ? [
                      {
                          range: new monaco.Range(selectedCodeLine.lineNumber, 1, selectedCodeLine.lineNumber, 1),
                          options: {
                              isWholeLine: true,
                              className: 'lved-preview-selected-line'
                          }
                      }
                  ]
                : []
        );

        if (shouldDecorate && !selectedCodeLine.openRequest) {
            this.scheduleSelectedLineDecorationClear();
        }
    }

    protected scheduleSelectedLineDecorationClear(): void {
        if (this.selectedLineClearTimer) {
            window.clearTimeout(this.selectedLineClearTimer);
        }
        this.selectedLineClearTimer = window.setTimeout(() => {
            this.selectedDecorations?.clear();
            this.selectedLineClearTimer = undefined;
        }, 1200);
    }

    protected normalizePath(path: string | undefined): string | undefined {
        if (!path) return undefined;
        const withoutScheme: string = path.startsWith('file://') ? decodeURIComponent(new URL(path).pathname) : path;
        return withoutScheme.replace(/\\/g, '/');
    }
}
